import { TextEditor } from "@/components/TextEditor";
import { useApi } from "@/hooks/use-api";
import { useFeedback } from "@/hooks/use-feedback";
import { useAppDispatch, useAppState } from "@/store/useApp";
import { CircleCheck } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router";
import { toast } from "sonner";
import { FeedbackHeader } from "./FeedbackHeader";
import { FeedbackFormatDialog } from "../documents/FeedbackFormatDialog";
import { useFeature } from "@/hooks/use-feature";

interface FeedbackProps {
  feedbackUnlocked: boolean;
  setFeedbackUnlocked: (unlocked: boolean) => void;
}

export const Feedback = ({
  feedbackUnlocked,
  setFeedbackUnlocked,
}: FeedbackProps) => {
  const dispatch = useAppDispatch();
  const document = useAppState((state) => state.currentDocument);
  const rubric = useAppState((state) => state.currentRubric);
  const grading = useAppState((state) => state.grading);
  const isPublishingFeedback = useAppState((state) => state.isPublishingFeedback);
  const { documentId } = useParams();
  const { saveFeedback, exportFeedback } = useApi();
  const { copyFeedback } = useFeedback();
  
  const isPublishingCurrentDocument = isPublishingFeedback;

  const [isSaving, setIsSaving] = useState(false);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const feedbackExportEnabled = useFeature("feedbackExport");
  const gradingIdentity = `${grading?.id ?? ""}:${grading?.feedback?.status ?? ""}`;
  const [trackedGradingIdentity, setTrackedGradingIdentity] = useState(gradingIdentity);
  const [originalContent, setOriginalContent] = useState(grading?.feedback?.summary ?? "");
  const [hasBeenEdited, setHasBeenEdited] = useState(false);

  if (trackedGradingIdentity !== gradingIdentity) {
    setTrackedGradingIdentity(gradingIdentity);
    setOriginalContent(grading?.feedback?.summary ?? "");
    setHasBeenEdited(false);
  }

  if (!document || !rubric) {
    return (
      <div className="text-gray-500 text-center py-8">
        No document or rubric available.
      </div>
    );
  }

  const allSuggestions =
    document.suggestions?.filter((s) => s.status === "approved") || [];

  if (allSuggestions.length === 0) {
    return (
      <div className="text-gray-500 text-center py-8">
        No feedback available yet. Add and approve suggestions to see feedback
        here.
      </div>
    );
  }

  const status = grading?.feedback?.status;
  const currentContent = grading?.feedback?.summary || "";
  const isPublished = status === "published";
  const isEditable = feedbackUnlocked || !isPublished;
  const contentWasEdited = hasBeenEdited && currentContent !== originalContent;

  const handleUnlock = () => {
    setFeedbackUnlocked(!feedbackUnlocked);
  };

  const handleContentChange = (content: string) => {
    dispatch({ type: "UPDATE_FEEDBACK_SUMMARY", payload: content });

    // Track if content has been edited
    setHasBeenEdited(content !== originalContent);
  };

  const handleSave = async () => {
    if (!documentId || !grading?.feedback?.summary) return;

    setIsSaving(true);
    try {
      const updatedGrading = await saveFeedback(
        documentId,
        grading.feedback.summary
      );
      // Update original content ref with the saved content
      if (updatedGrading?.feedback?.summary !== undefined) {
        setOriginalContent(updatedGrading.feedback.summary);
        setHasBeenEdited(false);
      }
      toast.success("Feedback saved successfully", {
        position: "bottom-right",
      });
    } catch {
      toast.error("Failed to save feedback", {
        description: "Please try again later.",
        icon: <CircleCheck className="h-4 w-4" />,
        position: "bottom-right",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCopyFeedback = () => {
    copyFeedback(currentContent);
  };

  const handleDownloadFeedback = () => {
    setFeedbackDialogOpen(true);
  };

  return (
    <div className="pb-40 max-w-2xl mx-auto flex-1">
      {documentId && (
        <FeedbackFormatDialog
          open={feedbackDialogOpen}
          onOpenChange={setFeedbackDialogOpen}
          documentId={documentId}
          exportFeedback={exportFeedback}
        />
      )}
      <FeedbackHeader
        status={status}
        isModified={contentWasEdited}
        isUnlocked={feedbackUnlocked}
        onSave={handleSave}
        onUnlock={handleUnlock}
        onDownload={handleDownloadFeedback}
        onCopyFeedback={handleCopyFeedback}
        isSaving={isSaving}
        currentContent={currentContent}
        feedbackExportEnabled={feedbackExportEnabled}
        isPublishingCurrentDocument={isPublishingCurrentDocument}
      />

      <TextEditor
        readOnly={!isEditable}
        className="border-none shadow-none leading-8 mb-10 text-base!"
        autoheight
        content={currentContent}
        scrollable
        onContentChange={handleContentChange}
      />
    </div>
  );
};
